import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/hec/components/ui/collapsible";
import { parseNavigationColor } from "~/hec/utils/color-contrast";

import { CategoryLink } from "./category-link";
import type { NavigationCategory } from "./types";

interface NavItemProps {
  category: NavigationCategory;
  /**
   * id → all-state ("Shop All") landing path (SFCC-11632). The "Shop All" link
   * for `category` resolves from this first, mirroring SFRA's
   * `menuItem.shopAll ? menuItem.shopAll : menuItem.url`: the parent's all-state
   * lives in `c_shopAll` for some categories (e.g. health-condition →
   * /health-condition/shop-all), not in `c_alternativeUrl`. Falls back to the
   * category's own `c_alternativeUrl`/path when the category has no entry.
   */
  categoryAltUrlMap?: Record<string, string>;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function NavItem({
  category,
  categoryAltUrlMap,
  isOpen,
  onToggle,
}: NavItemProps) {
  const subCategories =
    category.categories?.filter(item => item.c_showInMenu) ?? [];
  const hasParentLinkOnly = category.c_parentLinkOnly;
  // The category's "Shop All" target: its all-state landing (c_alternativeUrl
  // else c_shopAll, already host-normalized) when present, else its own
  // c_alternativeUrl so CategoryLink's `categoryUrlBuilder({id, path})` fallback
  // still applies.
  const shopAllUrl =
    categoryAltUrlMap?.[category.id] ?? category.c_alternativeUrl;
  // COM-581: BM "Navigation Link Color" → this category's OWN row only (the
  // parent trigger or the leaf link). Children resolve their own attribute on
  // recursion (a real last child named "Shop All" included); the synthesized
  // "Shop All" row never takes it (SFRA parity — categoryItems.isml applies it
  // to category rows, not the all-state link it appends).
  const highlight = parseNavigationColor(category.c_navigationColor);

  if (subCategories.length > 0 && !hasParentLinkOnly) {
    const lastIndex = subCategories.length - 1;
    let addShowAllLink = true;

    return (
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        {/* `group/nav-trigger`: the focusable element for the parent row, so a
            highlighted row's keyboard-focus darken keys off it (CategoryLink). */}
        <CollapsibleTrigger className="group/nav-trigger flex w-full [&[data-state=closed]_svg]:-rotate-90">
          <CategoryLink
            categoryId={category.id}
            categoryName={category.name ?? category.id}
            categoryPath={category.path}
            altCategoryUrl={category.c_alternativeUrl}
            hasSubCategories
            isOpen={isOpen}
            highlight={highlight}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="px-[0.9375rem]">
          {subCategories.map((subCategory, index) => {
            if (
              index === lastIndex &&
              subCategory.name?.toLowerCase() === "shop all"
            ) {
              addShowAllLink = false;
              return (
                <CategoryLink
                  key={subCategory.id}
                  categoryId={subCategory.id}
                  categoryName="Shop All"
                  categoryPath={subCategory.path}
                  altCategoryUrl={shopAllUrl}
                  shopAll
                  // A REAL catalog child named "Shop All" is a category row,
                  // so it takes its own BM colour (SFRA categoryItems.isml);
                  // only the synthesized all-state row below stays plain.
                  highlight={parseNavigationColor(
                    subCategory.c_navigationColor,
                  )}
                />
              );
            }
            return (
              <NavItem
                key={subCategory.id}
                category={subCategory}
                categoryAltUrlMap={categoryAltUrlMap}
              />
            );
          })}
          {addShowAllLink && (
            <CategoryLink
              categoryId={category.id}
              categoryName="Shop All"
              categoryPath={category.path}
              altCategoryUrl={shopAllUrl}
              shopAll
            />
          )}
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <CategoryLink
      categoryId={category.id}
      categoryName={category.name ?? category.id}
      categoryPath={category.path}
      altCategoryUrl={category.c_alternativeUrl}
      highlight={highlight}
    />
  );
}
